import React from "react";
import { InitialsAvatar } from "../chat/chatUi";
import { listAppUsers } from "../../services/userService";

/**
 * SP5 (Outlook-Rework) — Absender-Avatar mit Entra-Profilfoto:
 * Interne Absender (E-Mail ↔ App-User) zeigen ihr Microsoft-Foto (wie im Chat,
 * via `InitialsAvatar` → `/users/:id/photo`, 404 → Initialen); externe Absender
 * fallen auf Initialen + deterministische Farbe zurück.
 *
 * Das E-Mail→User-Mapping wird EINMAL pro Session aufgebaut (listAppUsers ist
 * serverseitig klein) und modul-weit gecacht — kein Fetch pro Zeile.
 */
let mapPromise: Promise<Map<string, string>> | null = null;
function internalUserIdMap(): Promise<Map<string, string>> {
    if (!mapPromise) {
        mapPromise = listAppUsers()
            .then((users) => {
                const m = new Map<string, string>();
                for (const u of users) {
                    const mail = (u.email ?? "").trim().toLowerCase();
                    if (mail) m.set(mail, u.id);
                }
                return m;
            })
            .catch(() => {
                mapPromise = null; // nächster Versuch darf neu laden
                return new Map<string, string>();
            });
    }
    return mapPromise;
}

/** Liefert die App-User-Id zur E-Mail-Adresse (null = extern/unbekannt). */
export function useInternalUserId(email?: string | null): string | null {
    const [id, setId] = React.useState<string | null>(null);
    React.useEffect(() => {
        const e = (email ?? "").trim().toLowerCase();
        if (!e) {
            setId(null);
            return;
        }
        let cancelled = false;
        void internalUserIdMap().then((m) => {
            if (!cancelled) setId(m.get(e) ?? null);
        });
        return () => {
            cancelled = true;
        };
    }, [email]);
    return id;
}

export const SenderAvatar: React.FC<{
    name: string;
    email?: string | null;
    size?: number;
    /** Fallback-Farbe für die Initialen (z. B. senderColor der Liste). */
    bg?: string;
}> = ({ name, email, size = 32, bg }) => {
    const userId = useInternalUserId(email);
    return <InitialsAvatar name={name || email || "?"} size={size} bg={bg} userId={userId} />;
};
